import React, { useEffect, useRef, useState } from 'react'
import ro from 'date-fns/locale/ro'
import PropTypes from 'prop-types'
import {
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Portal,
  Button,
  HStack,
  VStack,
  Popover,
  PopoverContent,
  PopoverTrigger,
  CloseButton
} from '@chakra-ui/react'
import { DayPicker } from 'react-day-picker'
import { format, getYear, isValid } from 'date-fns'
import 'react-day-picker/dist/style.css'
import {
  getCompleteDateRange,
  getTodayRange,
  getYesterdayRange,
  getLastSevenDaysRange,
  getLastThirtyDaysRange,
  getCurrentMonthRange,
  getLastMonthRange,
  getCurrentYearRange,
  getLastYearRange,
} from '../../helpers'
import './DatePicker.css'

const DateRangePicker = ({
  selected,
  onApply,
  dateFormat,
  placeholder,
  inputProps,
  allowReset,
  ...datePickerProps
}) => {
  const [tempRange, setTempRange] = useState(selected)
  const initRef = useRef()

  useEffect(() => {
    setTempRange(selected)
  }, [selected])

  const handleSelect = (val) => {
    setTempRange(val)
  }

  const handleReset = () => {
    setTempRange({ from: null, to: null })
  }

  const handleApply = () => {
    if (tempRange && isValid(tempRange.from) && isValid(tempRange.to)) {
      onApply(getCompleteDateRange(tempRange))
    } else {
      onApply(tempRange)
    }
  }

  return (
    <Popover isLazy initialFocusRef={initRef}>
      {({ onClose }) => (
        <>
          <PopoverTrigger>
            <InputGroup>
              <InputLeftElement pointerEvents="none" color="gray.400" fontSize="1rem">
                <i className="far fa-calendar-week" />
              </InputLeftElement>
              <Input
                value={
                  tempRange && isValid(tempRange.from)
                    ? isValid(tempRange.to)
                      ? `${format(tempRange.from, dateFormat)} - ${format(tempRange.to, dateFormat)}`
                      : `${format(tempRange.from, dateFormat)}`
                    : ''
                }
                placeholder={placeholder}
                isReadOnly={true}
                {...inputProps}
              />

              {allowReset && (
                <InputRightElement>
                  <CloseButton onClick={handleReset} size="sm" />
                </InputRightElement>
              )}
            </InputGroup>
          </PopoverTrigger>
          <Portal>
            <PopoverContent w="fit-content">
              <HStack spacing="0">
                <DayPicker
                  fromYear={2020}
                  toYear={getYear(new Date()) + 1}
                  captionLayout="dropdown"
                  locale={ro}
                  mode="range"

                  selected={tempRange}
                  onSelect={handleSelect}
                  {...datePickerProps}
                />
                <VStack spacing="2" p="2">
                  <Button
                    ref={initRef}
                    w="100%"
                    size="xs"
                    colorScheme="blue"
                    fontSize="sm"
                    onClick={() => setTempRange(getTodayRange())}
                  >
                    Azi
                  </Button>
                  <Button
                    w="100%"
                    size="xs"
                    colorScheme="blue"
                    fontSize="sm"
                    onClick={() => setTempRange(getYesterdayRange())}
                  >
                    Ieri
                  </Button>
                  <Button
                    w="100%"
                    colorScheme="blue"
                    size="xs"
                    fontSize="sm"
                    onClick={() => setTempRange(getLastSevenDaysRange())}
                  >
                    Ultimele 7 zile
                  </Button>
                  <Button
                    w="100%"
                    colorScheme="blue"
                    size="xs"
                    fontSize="sm"
                    onClick={() => setTempRange(getLastThirtyDaysRange())}
                  >
                    Ultimele 30 de zile
                  </Button>
                  <Button
                    w="100%"
                    colorScheme="blue"
                    size="xs"
                    fontSize="sm"
                    onClick={() => setTempRange(getCurrentMonthRange())}
                  >
                    Luna curenta
                  </Button>
                  <Button
                    w="100%"
                    colorScheme="blue"
                    size="xs"
                    fontSize="sm"
                    onClick={() => setTempRange(getLastMonthRange())}
                  >
                    Luna trecuta
                  </Button>
                  <Button
                    w="100%"
                    colorScheme="blue"
                    size="xs"
                    fontSize="sm"
                    onClick={() => setTempRange(getCurrentYearRange())}
                  >
                    Anul curent
                  </Button>
                  <Button
                    w="100%"
                    colorScheme="blue"
                    size="xs"
                    fontSize="sm"
                    onClick={() => setTempRange(getLastYearRange())}
                  >
                    Anul trecut
                  </Button>
                  {allowReset && (
                    <Button
                      w="100%"
                      size="xs"
                      variant="outline"
                      colorScheme="blue"
                      fontSize="sm"
                      onClick={handleReset}
                    >
                      Reset
                    </Button>
                  )}
                  <Button
                    w="100%"
                    size="xs"
                    colorScheme="green"
                    fontSize="sm"
                    onClick={() => {
                      handleApply()
                      onClose()
                    }}
                  >
                    Apply
                  </Button>
                </VStack>
              </HStack>
            </PopoverContent>
          </Portal>
        </>
      )}
    </Popover>
  )
}

DateRangePicker.propTypes = {
  selected: PropTypes.object,
  onApply: PropTypes.func.isRequired,
  dateFormat: PropTypes.string,
  placeholder: PropTypes.string,
  allowReset: PropTypes.bool
}

DateRangePicker.defaultProps = {
  dateFormat: 'dd.MM.yyyy',
  placeholder: 'Alege o perioada',
  allowReset: true,
  selected: { from: null, to: null }
}

export default DateRangePicker
